import React, { useEffect, useState } from "react";
import { collection, addDoc, doc, getDoc, updateDoc } from "firebase/firestore";
import { toast } from "react-toastify";
import { db } from "../../config/firebase";
import { api } from "../../config/api";
import { Form, Input, Select, Button, Typography } from 'antd';
import { UserOutlined, MailOutlined, LockOutlined, HomeOutlined, GlobalOutlined, NumberOutlined, BankOutlined } from '@ant-design/icons';
import { useQueryClient } from "@tanstack/react-query";

const { Title } = Typography;

const currencies = [
  { currency: "USD", symbol: "$" },
  { currency: "EUR", symbol: "€" },
];

const EditUserForm = ({ setOpenModel, id }) => {
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [userData, setUserData] = useState({
    name: "",
    email: "",
    address: "",
    city: "",
    postalCode: "",
    country: "",
    password: "",
    role: "user",
    vatNumber: "",
    billToText: "",
    currency: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userDocRef = doc(db, "users", id);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const data = userDocSnap.data();
          setUserData(data);
          form.setFieldsValue(data); // Set form values
        } else {
          toast.error("User data not found");
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchData();
  }, [id, form]);

  const validatePostalCode = (_, value) => {
    if (!value || /^\d+$/.test(value)) {
      return Promise.resolve();
    }
    return Promise.reject(new Error("Postal code should contain only numbers"));
  };

  const validatePassword = (_, value) => {
    // For edit form, we can allow empty password (no change)
    if (!value) {
      return Promise.resolve();
    }
    if (!/^(?=.*[!@#$%^&*(),.?":{}|<>])(?=.*\d).{6,20}$/.test(value)) {
      return Promise.reject(new Error("Password must be 6-20 characters long and contain at least one special character and one number"));
    }
    return Promise.resolve();
  };

  const handleUpdateData = async (values) => {
    try {
      setLoading(true);
      // Preserve the role from userData
      const updatedData = { ...values, role: userData.role };
      
      await api.put(`/users/${id}`, updatedData);
      queryClient.invalidateQueries(["users"]);

      toast.success("User data updated successfully!");
      setOpenModel(false);
    } catch (error) {
      toast.error("Error updating data: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="md:w-[500px] w-full">
      {/* Centered title using flexbox */}
      <div className="flex justify-center mb-4">
        <Title level={3} className="m-0">Edit User</Title>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleUpdateData}
        initialValues={userData}
        className="mt-3 w-full"
      >
        <div className="space-y-4">
          <div className="flex items-center">
            <label className="w-[120px] font-medium">
              <span className="text-red-500 mr-1">*</span>Name:
            </label>
            <Form.Item
              name="name"
              rules={[{ required: true, message: 'Please enter name' }]}
              className="flex-1 mb-0"
            >
              <Input prefix={<UserOutlined />} placeholder="Enter Name" />
            </Form.Item>
          </div>

          <div className="flex items-center">
            <label className="w-[120px] font-medium">
              <span className="text-red-500 mr-1">*</span>Email:
            </label>
            <Form.Item
              name="email"
              rules={[
                { required: true, message: 'Please enter email' },
                { type: 'email', message: 'Please enter a valid email' }
              ]}
              className="flex-1 mb-0"
            >
              <Input prefix={<MailOutlined />} placeholder="Enter Email" readOnly/>
            </Form.Item>
          </div>

          {/* <div className="flex items-center">
            <label className="w-[120px] font-medium"><span className="text-red-500 mr-1">*</span>Password:</label>
            <Form.Item
              name="password"
              rules={[{ validator: validatePassword }]}
              className="flex-1 mb-0"
            >
              <Input.Password prefix={<LockOutlined />} placeholder="Enter Password (Leave blank to keep current)" />
            </Form.Item>
          </div> */}

          <div className="flex items-center">
            <label className="w-[120px] font-medium">
              <span className="text-red-500 mr-1">*</span>Address:
            </label>
            <Form.Item
              name="address"
              rules={[{ required: true, message: 'Please enter address' }]}
              className="flex-1 mb-0"
            >
              <Input prefix={<HomeOutlined />} placeholder="Enter Address" />
            </Form.Item>
          </div>

          <div className="flex items-center">
            <label className="w-[120px] font-medium">
              <span className="text-red-500 mr-1">*</span>City:
            </label>
            <Form.Item
              name="city"
              rules={[{ required: true, message: 'Please enter city' }]}
              className="flex-1 mb-0"
            >
              <Input prefix={<HomeOutlined />} placeholder="Enter City" />
            </Form.Item>
          </div>

          <div className="flex items-center">
            <label className="w-[120px] font-medium">
              <span className="text-red-500 mr-1">*</span>Postal Code:
            </label>
            <Form.Item
              name="postalCode"
              rules={[
                { required: true, message: 'Please enter postal code' },
                // { validator: validatePostalCode }
              ]}
              className="flex-1 mb-0"
            >
              <Input prefix={<NumberOutlined />} placeholder="Enter Postal Code" />
            </Form.Item>
          </div>

          <div className="flex items-center">
            <label className="w-[120px] font-medium">
              <span className="text-red-500 mr-1">*</span>Country:
            </label>
            <Form.Item
              name="country"
              rules={[{ required: true, message: 'Please enter country' }]}
              className="flex-1 mb-0"
            >
              <Input prefix={<GlobalOutlined />} placeholder="Enter Country" />
            </Form.Item>
          </div>

          <div className="flex items-center">
            <label className="w-[120px] font-medium">VAT Number:</label>
            <Form.Item
              name="vatNumber"
              className="flex-1 mb-0"
            >
              <Input prefix={<BankOutlined />} placeholder="Enter VAT Number (Optional)" />
            </Form.Item>
          </div>

          <div className="flex items-start">
            <label className="w-[120px] pt-2 font-medium">Bill To:</label>
            <Form.Item name="billToText" className="flex-1 mb-0">
              <Input.TextArea rows={3} placeholder="Enter bill-to details" />
            </Form.Item>
          </div>

          <div className="flex items-center">
            <label className="w-[120px] font-medium">
              <span className="text-red-500 mr-1">*</span>Currency:
            </label>
            <Form.Item
              name="currency"
              rules={[{ required: true, message: 'Please select currency' }]}
              className="flex-1 mb-0"
            >
              <Select 
                placeholder="Select Currency"
                showSearch
                optionFilterProp="children"
              >
                {currencies.map(({ currency, symbol }) => (
                  <Select.Option key={currency} value={currency}>
                    {symbol} {currency}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <div className="flex justify-end">
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              className="bg-blue-600 hover:bg-blue-700"
              shape="round"
              size="large"
              style={{ width: '200px' }}
            >
              {loading ? "Updating..." : "Update User"}
            </Button>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default EditUserForm;
